// src/pages/task/TaskCreatePage.tsx
// Writing down a task.
//
// Two columns, split by who does the work. The left column is what a person
// *writes*: the key the task will get, the title, the description, and the
// secondary blocks (checklists, watchers, attachments, custom fields) waiting
// behind an add-action. The right column is what a person *chooses*: an
// always-open panel with one labelled row per property, so nothing about the
// task is hidden behind a pill you have to think to click.
//
// A panel row never disappears. An unset property reads "not set" in muted
// text, a required one is marked before Create is ever pressed, and the footer
// says how many required fields are still missing. A source that came back
// empty still leaves its row — it simply has nothing to offer inside.
//
// Nothing about the system is explained on the page. The parent scope decides
// which types, templates, categories and numbering apply (via
// `ResolveTaskContext`), and that sentence is said once, inside the parent's
// own popover, where it is an answer instead of a lecture.
//
// The default parent is "no parent" — a real, chosen state that sends no
// `parent` at all. `ResolveTaskContext` still needs somewhere to resolve
// against, so the tenant is sent as the *resolution* scope while the created
// task carries nothing. The two are different questions and stay different in
// the payload.
//
// Fast path: title + Create, reachable in seconds, ⌘/Ctrl+Enter submits.
// Everything typed is mirrored into a per-scope sessionStorage draft, so an
// accidental navigation costs nothing.

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Alert, Button, ErrorAlert } from '@react/components';
import type { CustomFieldDefinition } from '@core/types/task';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TagPicker } from '@/components/TagPicker';
import { UserPicker } from '@/components/UserPicker';
import { AutoGrowTextarea, TitleInput } from '@/components/form';

import {
  getAvatarColor,
  getEntityId,
  getInitials,
  getPriorityColor,
  getPriorityIconPath,
  taskPriorityOptions,
} from '@/features/task/logic';
import {
  AddAction,
  AttachmentDropzone,
  ChecklistDraftEditor,
  CREATE_ICONS,
  CreateSection,
  CustomFieldControl,
  Icon,
  PillOptionList,
  PropertyPill,
  PropertyRow,
  SECTION_ATTACHMENTS,
  SECTION_CHECKLISTS,
  SECTION_WATCHERS,
  TaskTypeStrip,
  TemplateMenu,
  customFieldId,
  customFieldSectionKey,
  typeDefIconPath,
  typeDefId,
  useTaskCreateForm,
  PARENT_KINDS,
} from '@/features/task/create';
import type { ParentKind } from '@/features/task/create';

/** DOM id of the title field — also the focus target when the title is invalid. */
const TITLE_FIELD_ID = 'task-create-title';

/**
 * True when the custom field renders a single labelable control carrying the id.
 * The user picker and the chip multi-select render composites instead, so
 * pointing a `<label for>` at them would be a dangling reference — they carry
 * their own accessible name.
 */
function labelableCustomField(def: CustomFieldDefinition): boolean {
  return (
    def.field_type !== 'CUSTOM_FIELD_TYPE_USER' &&
    def.field_type !== 'CUSTOM_FIELD_TYPE_MULTI_SELECT'
  );
}

export const TaskCreatePage = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();

  // A deep link may name the parent; nothing else does. The default is "no
  // parent" — a real, chosen state that sends no `parent` at all.
  const urlParentId = searchParams.get('parent_id') || searchParams.get('project') || '';
  const urlParentType = searchParams.get('parent_type') || (urlParentId ? 'project' : 'none');
  const [initialParent] = useState(() => ({
    kind: ((PARENT_KINDS as readonly string[]).includes(urlParentType)
      ? urlParentType
      : 'entity') as ParentKind,
    id: urlParentId,
    typeName: (PARENT_KINDS as readonly string[]).includes(urlParentType) ? '' : urlParentType,
  }));
  const [urlTemplateId] = useState(() => searchParams.get('template'));

  // Everything about *what a task is* comes from the shared hook — the same
  // one the quick-create drawer calls, so the two surfaces cannot drift.
  const f = useTaskCreateForm({
    initialParent,
    templateId: urlTemplateId,
    titleFieldId: TITLE_FIELD_ID,
    // On the full page, creating a task means going to it: this surface is for
    // considered work, and the thing you considered is now worth opening.
    onCreated: (_created: unknown, createdId: string) =>
      navigate(createdId ? `/tasks/${createdId}` : '/tasks'),
  });

  const {
    store,
    customFieldsLoading,
    descriptionRef,
    formRef,
    touched,
    parentKind,
    setParentKind,
    parentId,
    setParentId,
    otherTypeName,
    setOtherTypeName,
    parentKinds,
    parentOptions,
    parentIsLoading,
    parentLoadError,
    retryParents,
    parentValue,
    parentDisplayName,
    pickerScope,
    form,
    setField,
    setAssignee,
    errors,
    errorText,
    isSubmitting,
    submit,
    submitNow,
    canSubmit,
    missingRequired,
    availableTypes,
    selectedType,
    setSelectedTypeId,
    typeLabel,
    categories,
    categoryId,
    setCategoryId,
    categoryValue,
    capabilities,
    priorityValue,
    priorityIcon,
    priorityIconClass,
    tags,
    setTags,
    tagsValue,
    formatDate,
    contextLoading,
    contextError,
    retryContext,
    templateEntries,
    selectedTemplateId,
    applyTemplate,
    templateDropped,
    numberingScheme,
    keyPreview,
    customFieldGroups,
    cfValues,
    setCustomField,
    filledInGroup,
    checklists,
    setChecklists,
    checklistItemCount,
    watchers,
    setWatchers,
    pendingFiles,
    setPendingFiles,
    handleAddFiles,
    uploadWarning,
    advancedPreset,
    sectionOpen,
    toggleSection,
    descriptionPreview,
    setDescriptionPreview,
    isDirty,
    draftRestored,
    setDraftRestored,
    discardDraft,
  } = f;

  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // ⌘/Ctrl+Enter submits from anywhere in the form. Escape is deliberately not
  // bound — it must never throw away what is being typed.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void submitNow();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [submitNow]);

  const notSet = t('common.notSet');

  const dateInputClass =
    'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground ' +
    'focus:outline-none focus:ring-2 focus:ring-ring';

  const clearButtonClass =
    'self-start rounded-sm text-xs text-muted-foreground underline-offset-2 ' +
    'hover:text-destructive hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  // ── The screen, block by block, in the order it is read. ───────────────

  // The title: the loudest thing here, and where the caret lands on mount.
  const titleBlock: ReactNode = (
    <div data-field-error={errors.title ? 'true' : undefined}>
      {keyPreview && (
        <p
          className="mb-1 font-mono text-xs text-muted-foreground"
          title={numberingScheme?.name || t('task.createForm.keyPreview')}
        >
          {keyPreview}
        </p>
      )}
      <TitleInput
        id={TITLE_FIELD_ID}
        size="lg"
        value={form.title}
        onChange={(value) => setField('title', value)}
        placeholder={t('task.createForm.titlePlaceholder')}
        initialFocus
        aria-label={t('task.field.title')}
        onEnter={() => descriptionRef.current?.focus()}
      />
      {errors.title && (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {errorText('title')}
        </p>
      )}
    </div>
  );

  // The description: borderless and auto-growing. The preview toggle appears
  // only once there is something to preview — no Write/Preview band.
  const descriptionBlock: ReactNode = (
    <div className="relative">
      {form.description.trim() && (
        <button
          type="button"
          onClick={() => setDescriptionPreview((prev) => !prev)}
          aria-pressed={descriptionPreview}
          aria-label={t('task.createForm.togglePreview')}
          title={t('task.createForm.togglePreview')}
          className="absolute right-0 top-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon
            d={descriptionPreview ? CREATE_ICONS.pencil : CREATE_ICONS.eye}
            className="h-4 w-4"
          />
        </button>
      )}
      {descriptionPreview ? (
        <div className="prose prose-sm min-h-16 max-w-none pr-8 text-foreground dark:prose-invert">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.description}</ReactMarkdown>
        </div>
      ) : (
        <AutoGrowTextarea
          ref={descriptionRef}
          id="task-create-description"
          value={form.description}
          onChange={(value) => setField('description', value)}
          placeholder={t('task.createForm.descriptionPlaceholder')}
          aria-label={t('task.field.description')}
          minRows={3}
          className={form.description.trim() ? 'pr-8' : undefined}
        />
      )}
    </div>
  );

  // ─── The properties panel ───────────────────────────────────────────────
  // Always open, one labelled row per property, in the order people ask the
  // questions: where does it live, what is it, how urgent, who, when, how big.

  const parentRow: ReactNode = (
    <PropertyRow label={t('task.createForm.pill.parent')}>
      <PropertyPill
        variant="row"
        icon={CREATE_ICONS.folder}
        label={t('task.createForm.pill.parent')}
        value={parentValue}
        emptyText={notSet}
        disabled={isSubmitting}
        popoverTitle={t('task.field.parent')}
        popoverHint={t('task.createForm.parentDrivesForm', { parent: parentDisplayName })}
        popoverWidth={320}
      >
        {() => (
          <div className="flex flex-col gap-3">
            <PillOptionList
              ariaLabel={t('task.createForm.parentType')}
              value={parentKind}
              options={parentKinds.map((kind) => ({
                value: kind,
                label: t(`task.createForm.parent.${kind}`),
              }))}
              onSelect={(kind) => {
                setParentKind(kind as ParentKind);
                setParentId('');
                applyTemplate(null);
              }}
            />

            {parentKind === 'entity' && (
              <div className="flex flex-col gap-2 border-t border-border pt-2">
                <label
                  htmlFor="task-create-parent-type"
                  className="text-xs text-muted-foreground"
                >
                  {t('task.createForm.parentEntityType')}
                </label>
                <input
                  id="task-create-parent-type"
                  type="text"
                  value={otherTypeName}
                  onChange={(e) => setOtherTypeName(e.target.value)}
                  placeholder={t('task.createForm.parentEntityTypePlaceholder')}
                  className={dateInputClass}
                />
                <label htmlFor="task-create-parent-id" className="text-xs text-muted-foreground">
                  {t('task.field.parent')}
                </label>
                <input
                  id="task-create-parent-id"
                  type="text"
                  value={parentId}
                  onChange={(e) => {
                    setParentId(e.target.value);
                    applyTemplate(null);
                  }}
                  placeholder={t('task.createForm.entityIdPlaceholder')}
                  className={`${dateInputClass} font-mono placeholder:font-sans`}
                />
              </div>
            )}

            {(parentKind === 'project' || parentKind === 'team') && (
              <div className="border-t border-border pt-2">
                {parentIsLoading ? (
                  <div
                    className="h-8 w-full motion-safe:animate-pulse rounded-md bg-muted"
                    aria-busy="true"
                  />
                ) : parentLoadError ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs text-destructive">{t('task.createForm.parentLoadFailed')}</p>
                    <button
                      type="button"
                      onClick={retryParents}
                      className="rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {t('common.retry')}
                    </button>
                  </div>
                ) : parentOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('task.createForm.noParentOptions')}
                  </p>
                ) : (
                  <PillOptionList
                    ariaLabel={t('task.field.parent')}
                    value={parentId}
                    options={parentOptions}
                    onSelect={(value) => {
                      setParentId(value);
                      applyTemplate(null);
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const typeRow: ReactNode = (
    <PropertyRow label={t('task.createForm.pill.type')}>
      <PropertyPill
        variant="row"
        icon={typeDefIconPath(selectedType)}
        swatch={selectedType?.color || undefined}
        label={t('task.createForm.pill.type')}
        value={typeLabel(selectedType)}
        emptyText={notSet}
        disabled={isSubmitting}
        popoverTitle={t('task.field.type')}
        popoverHint={selectedType?.description || undefined}
        popoverWidth={320}
      >
        {(close) => (
          <>
            <TaskTypeStrip
              types={availableTypes}
              value={typeDefId(selectedType)}
              onChange={(id) => {
                touched.current.add('type');
                setSelectedTypeId(id);
                close();
              }}
              isLoading={contextLoading}
              error={contextError}
              onRetry={() => retryContext()}
              disabled={isSubmitting}
            />
            <Link
              to="/settings/task-types"
              target="_blank"
              className="rounded-sm text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('common.manage')}
            </Link>
          </>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const categoryRow: ReactNode = (
    <PropertyRow label={t('task.createForm.pill.category')}>
      <PropertyPill
        variant="row"
        icon={CREATE_ICONS.grid}
        label={t('task.createForm.pill.category')}
        value={categoryValue}
        emptyText={notSet}
        disabled={isSubmitting}
        popoverTitle={t('task.categories.title')}
      >
        {(close) =>
          contextLoading && categories.length === 0 ? (
            <div
              className="h-8 w-full motion-safe:animate-pulse rounded-md bg-muted"
              aria-busy="true"
            />
          ) : contextError && categories.length === 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-destructive">{t('task.createForm.contextError')}</p>
              <button
                type="button"
                onClick={() => retryContext()}
                className="rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('common.retry')}
              </button>
            </div>
          ) : categories.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('task.createForm.noCategories')}</p>
          ) : (
            <PillOptionList
              ariaLabel={t('task.categories.title')}
              value={categoryId}
              options={[
                { value: '', label: t('task.createForm.noCategory') },
                ...categories.map((category) => ({
                  value: getEntityId(category),
                  label: category.name,
                  hint: category.code || undefined,
                })),
              ]}
              onSelect={(value) => {
                setCategoryId(value);
                close();
              }}
            />
          )
        }
      </PropertyPill>
    </PropertyRow>
  );

  const priorityRow: ReactNode = (
    <PropertyRow label={t('task.createForm.pill.priority')}>
      <PropertyPill
        variant="row"
        icon={priorityIcon}
        iconClassName={priorityIconClass}
        label={t('task.createForm.pill.priority')}
        value={priorityValue}
        emptyText={notSet}
        disabled={isSubmitting}
        popoverTitle={t('task.field.priority')}
      >
        {(close) => (
          <PillOptionList
            ariaLabel={t('task.field.priority')}
            value={form.priority}
            options={taskPriorityOptions.map((option) => ({
              value: option.value,
              label: t(option.labelKey),
              icon: getPriorityIconPath(option.value),
              iconClassName: getPriorityColor(option.value),
            }))}
            onSelect={(value) => {
              setField('priority', value);
              close();
            }}
          />
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const assigneeRow: ReactNode = (
    <PropertyRow
      label={t('task.createForm.pill.assignee')}
      required={capabilities.requiresAssignee}
      error={errorText('assignee')}
      errorId="task-create-assignee-error"
    >
      <PropertyPill
        variant="row"
        icon={CREATE_ICONS.user}
        label={t('task.createForm.pill.assignee')}
        value={form.assigneeName}
        emptyText={notSet}
        required={capabilities.requiresAssignee}
        invalid={Boolean(errors.assignee)}
        errorId="task-create-assignee-error"
        disabled={isSubmitting}
        popoverTitle={t('task.field.assignee')}
        popoverWidth={320}
      >
        {() => (
          <>
            <UserPicker
              value={form.assignee}
              onChange={setAssignee}
              placeholder={t('task.field.assigneePlaceholder')}
              scope={pickerScope}
            />
            {form.assignee && (
              <button
                type="button"
                onClick={() => setAssignee('', '')}
                className={clearButtonClass}
              >
                {t('common.clear')}
              </button>
            )}
          </>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const dueDateRow: ReactNode = (
    <PropertyRow
      label={t('task.field.dueDate')}
      error={errorText('due_date')}
      errorId="task-create-due-error"
    >
      <PropertyPill
        variant="row"
        icon={CREATE_ICONS.calendar}
        label={t('task.field.dueDate')}
        value={formatDate(form.due_date)}
        emptyText={notSet}
        invalid={Boolean(errors.due_date)}
        errorId="task-create-due-error"
        disabled={isSubmitting}
        popoverTitle={t('task.field.dueDate')}
        popoverWidth={240}
      >
        {() => (
          <div className="flex flex-col gap-2">
            <label htmlFor="task-create-due-date" className="sr-only">
              {t('task.field.dueDate')}
            </label>
            <input
              id="task-create-due-date"
              type="date"
              value={form.due_date}
              onChange={(e) => setField('due_date', e.target.value)}
              className={dateInputClass}
            />
            {form.due_date && (
              <button type="button" onClick={() => setField('due_date', '')} className={clearButtonClass}>
                {t('common.clear')}
              </button>
            )}
          </div>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const startDateRow: ReactNode = (
    <PropertyRow label={t('task.startDate')}>
      <PropertyPill
        variant="row"
        icon={CREATE_ICONS.calendar}
        label={t('task.startDate')}
        value={formatDate(form.start_date)}
        emptyText={notSet}
        disabled={isSubmitting}
        popoverTitle={t('task.startDate')}
        popoverWidth={240}
      >
        {() => (
          <div className="flex flex-col gap-2">
            <label htmlFor="task-create-start-date" className="sr-only">
              {t('task.startDate')}
            </label>
            <input
              id="task-create-start-date"
              type="date"
              value={form.start_date}
              onChange={(e) => setField('start_date', e.target.value)}
              className={dateInputClass}
            />
            {form.start_date && (
              <button
                type="button"
                onClick={() => setField('start_date', '')}
                className={clearButtonClass}
              >
                {t('common.clear')}
              </button>
            )}
          </div>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  // Estimate exists only where the type tracks time — a field that would be
  // ignored on save is worse than one that is absent.
  const estimateRow: ReactNode = capabilities.timeTracking && (
    <PropertyRow label={t('task.createForm.pill.estimate')}>
      <PropertyPill
        variant="row"
        icon={CREATE_ICONS.clock}
        label={t('task.createForm.pill.estimate')}
        value={
          form.estimated_hours ? t('task.createForm.hoursShort', { hours: form.estimated_hours }) : ''
        }
        emptyText={notSet}
        disabled={isSubmitting}
        popoverTitle={t('task.estimatedHours')}
        popoverWidth={220}
      >
        {() => (
          <div className="flex flex-col gap-2">
            <label htmlFor="task-create-estimate" className="sr-only">
              {t('task.estimatedHours')}
            </label>
            <input
              id="task-create-estimate"
              type="text"
              inputMode="decimal"
              value={form.estimated_hours}
              onChange={(e) => setField('estimated_hours', e.target.value)}
              placeholder="—"
              className={`${dateInputClass} tabular-nums`}
            />
            {form.estimated_hours && (
              <button
                type="button"
                onClick={() => setField('estimated_hours', '')}
                className={clearButtonClass}
              >
                {t('task.clearEstimatedHours')}
              </button>
            )}
          </div>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const tagsRow: ReactNode = (
    <PropertyRow label={t('task.createForm.pill.tags')}>
      <PropertyPill
        variant="row"
        icon={CREATE_ICONS.tag}
        label={t('task.createForm.pill.tags')}
        value={tagsValue}
        emptyText={notSet}
        disabled={isSubmitting}
        popoverTitle={t('task.field.tags')}
        popoverWidth={320}
      >
        {() => (
          <TagPicker
            value={tags}
            onChange={setTags}
            placeholder={t('tag.selectTags')}
          />
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const propertiesPanel: ReactNode = (
    <aside
      aria-labelledby="task-create-properties-heading"
      className="lg:sticky lg:top-4 lg:self-start"
    >
      <h2
        id="task-create-properties-heading"
        className="mb-2 px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground"
      >
        {t('task.createForm.properties')}
      </h2>
      <div className="flex flex-col gap-2.5 rounded-lg border border-border bg-muted/20 p-2">
        {parentRow}
        {typeRow}
        {categoryRow}
        {priorityRow}
        {assigneeRow}
        {dueDateRow}
        {startDateRow}
        {estimateRow}
        {tagsRow}
      </div>
    </aside>
  );

  // The offers: one row of add-actions, and only what this type enables.
  const addActionsRow: ReactNode = (
    <div className="flex flex-wrap items-center gap-1 border-t border-border pt-3">
      {capabilities.checklists && (
        <AddAction
          icon={CREATE_ICONS.checklist}
          label={t('task.checklists')}
          count={checklistItemCount}
          open={sectionOpen(SECTION_CHECKLISTS, checklists.length > 0)}
          onToggle={() => toggleSection(SECTION_CHECKLISTS, checklists.length > 0)}
          controls="task-create-section-checklists"
          disabled={isSubmitting}
        />
      )}
      {capabilities.watchers && (
        <AddAction
          icon={CREATE_ICONS.users}
          label={t('task.watchers')}
          count={watchers.length}
          open={sectionOpen(SECTION_WATCHERS, watchers.length > 0)}
          onToggle={() => toggleSection(SECTION_WATCHERS, watchers.length > 0)}
          controls="task-create-section-watchers"
          disabled={isSubmitting}
        />
      )}
      <AddAction
        icon={CREATE_ICONS.paperclip}
        label={t('task.attachments')}
        count={pendingFiles.length}
        open={sectionOpen(SECTION_ATTACHMENTS, pendingFiles.length > 0)}
        onToggle={() => toggleSection(SECTION_ATTACHMENTS, pendingFiles.length > 0)}
        controls="task-create-section-attachments"
        disabled={isSubmitting}
      />
      {customFieldGroups.map((group) => {
        const key = customFieldSectionKey(group);
        const holdsContent =
          filledInGroup(group) > 0 ||
          advancedPreset ||
          group.fields.some((def) => def.is_required);
        return (
          <AddAction
            key={key}
            icon={CREATE_ICONS.sliders}
            label={group.key || t('task.customFields')}
            count={filledInGroup(group)}
            open={sectionOpen(key, holdsContent)}
            onToggle={() => toggleSection(key, holdsContent)}
            controls={`task-create-section-${key}`}
            disabled={isSubmitting}
          />
        );
      })}
      {customFieldsLoading && customFieldGroups.length === 0 && (
        <span
          className="h-7 w-24 motion-safe:animate-pulse rounded-md bg-muted"
          aria-busy="true"
        />
      )}
    </div>
  );

  // What those add-actions opened, inline and in the same order.
  const disclosedSections: ReactNode = (
    <>
      {capabilities.checklists && sectionOpen(SECTION_CHECKLISTS, checklists.length > 0) && (
        <CreateSection
          id="task-create-section-checklists"
          title={t('task.checklists')}
          hint={t('task.createForm.checklistsHint')}
        >
          <ChecklistDraftEditor checklists={checklists} onChange={setChecklists} />
        </CreateSection>
      )}

      {capabilities.watchers && sectionOpen(SECTION_WATCHERS, watchers.length > 0) && (
        <CreateSection
          id="task-create-section-watchers"
          title={t('task.watchers')}
          hint={t('task.createForm.watchersHint')}
        >
          {watchers.length > 0 && (
            <ul className="mb-2 flex flex-wrap gap-1.5">
              {watchers.map((watcher) => (
                <li
                  key={watcher.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground"
                >
                  <span
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-medium text-white"
                    style={{ backgroundColor: getAvatarColor(watcher.name) }}
                    aria-hidden="true"
                  >
                    {getInitials(watcher.name)}
                  </span>
                  {watcher.name}
                  <button
                    type="button"
                    onClick={() => setWatchers((prev) => prev.filter((w) => w.id !== watcher.id))}
                    aria-label={t('task.createForm.removeWatcher', { name: watcher.name })}
                    className="rounded-sm text-muted-foreground hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Icon d={CREATE_ICONS.x} className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <UserPicker
            value=""
            onChange={(userId, displayName) => {
              if (!userId || watchers.some((w) => w.id === userId)) return;
              setWatchers((prev) => [...prev, { id: userId, name: displayName }]);
            }}
            placeholder={t('task.addWatcher')}
            scope={pickerScope}
          />
        </CreateSection>
      )}

      {sectionOpen(SECTION_ATTACHMENTS, pendingFiles.length > 0) && (
        <CreateSection id="task-create-section-attachments" title={t('task.attachments')}>
          <AttachmentDropzone
            files={pendingFiles}
            onAdd={handleAddFiles}
            onRemove={(id) => setPendingFiles((prev) => prev.filter((f) => f.id !== id))}
            disabled={isSubmitting}
          />
        </CreateSection>
      )}

      {/* Custom fields — grouped by `group`, ordered by `position`. */}
      {customFieldGroups.map((group) => {
        const key = customFieldSectionKey(group);
        const holdsContent =
          filledInGroup(group) > 0 || advancedPreset || group.fields.some((def) => def.is_required);
        if (!sectionOpen(key, holdsContent)) return null;
        return (
          <CreateSection
            key={key}
            id={`task-create-section-${key}`}
            title={group.key || t('task.customFields')}
            aside={
              <Link
                to="/tasks/settings"
                target="_blank"
                className="shrink-0 rounded-sm text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('common.manage')}
              </Link>
            }
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {group.fields.map((def) => {
                const fieldId = customFieldId(def);
                const domId = `task-create-cf-${fieldId}`;
                const message = errorText(fieldId);
                return (
                  <div key={fieldId} data-field-error={message ? 'true' : undefined}>
                    <label
                      htmlFor={labelableCustomField(def) ? domId : undefined}
                      className="mb-1 block text-xs text-muted-foreground"
                    >
                      {def.display_name}
                      {def.is_required && (
                        <span className="ml-1 text-destructive" aria-hidden="true">
                          *
                        </span>
                      )}
                    </label>
                    <CustomFieldControl
                      def={def}
                      id={domId}
                      value={cfValues[fieldId]}
                      error={message}
                      projectId={parentKind === 'project' ? parentId : undefined}
                      teamId={parentKind === 'team' ? parentId : undefined}
                      onChange={(value, label) => setCustomField(fieldId, value, label)}
                    />
                  </div>
                );
              })}
            </div>
          </CreateSection>
        );
      })}
    </>
  );

  // Anything that went wrong, or that will not survive the chosen path, kept
  // beside the button that acts on it.
  const alerts: ReactNode = (contextError ||
    uploadWarning ||
    templateDropped.length > 0 ||
    !!store.error) && (
    <div className="space-y-2">
      {contextError && (
        <Alert variant="warning">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex-1">{t('task.createForm.contextDegraded')}</span>
            <button
              type="button"
              onClick={() => retryContext()}
              className="rounded-md px-2 py-1 text-sm font-medium underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('common.retry')}
            </button>
          </div>
        </Alert>
      )}
      {templateDropped.length > 0 && (
        <Alert variant="warning">
          {t('task.createForm.templateDrops', { fields: templateDropped.join(', ') })}
        </Alert>
      )}
      {uploadWarning && <Alert variant="warning">{t('task.createForm.attachmentsFailed')}</Alert>}
      {!!store.error && (
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      )}
    </div>
  );

  // The action bar, pinned so Create is always one click away — and saying what
  // is still missing before it is pressed.
  const actionBar: ReactNode = (
    <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <p className="min-w-0 flex-1 text-xs" aria-live="polite">
        {missingRequired.length > 0 ? (
          <span className="text-destructive">
            {t('task.createForm.requiredRemaining', {
              count: missingRequired.length,
              fields: missingRequired.join(', '),
            })}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('task.submitHint')}</span>
        )}
      </p>
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={() => (isDirty ? setConfirmDiscard(true) : navigate('/tasks'))}
        >
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!canSubmit} isLoading={isSubmitting}>
          {t('task.create')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-5xl pb-24">
      {/* ── Heading row: where you are, and the one way in that is not
             "start typing" ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <nav
          aria-label={t('layout.navbar.breadcrumb')}
          className="flex items-center gap-2 text-sm"
        >
          <Link
            to="/tasks"
            className="rounded-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('task.list')}
          </Link>
          <span className="text-muted-foreground" aria-hidden="true">
            /
          </span>
          <h1 aria-current="page" className="font-medium text-foreground">
            {t('task.create')}
          </h1>
        </nav>

        <TemplateMenu
          entries={templateEntries}
          value={selectedTemplateId}
          onSelect={applyTemplate}
          parentLabel={parentDisplayName}
          createHref={`/task-templates/new${parentId ? `?project=${parentId}` : ''}`}
        />
      </div>

      {/* A restored draft explains why the fields are not empty, so it sits
          with them rather than at the bottom of the page. */}
      {draftRestored && (
        <div role="status" className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <Icon d={CREATE_ICONS.restore} className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="text-muted-foreground">{t('task.createForm.draftRestored')}</span>
          <button
            type="button"
            onClick={discardDraft}
            className="rounded-sm font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('task.createForm.discardDraft')}
          </button>
          <button
            type="button"
            onClick={() => setDraftRestored(false)}
            aria-label={t('common.close')}
            className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Icon d={CREATE_ICONS.x} className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="mt-4 space-y-5"
      >
        {/* Two columns: what you write, and what you choose. Below `lg` the
            panel stacks under the composer rather than squeezing beside it. */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-8">
          <div className="min-w-0 space-y-5">
            {titleBlock}
            {descriptionBlock}
            {addActionsRow}
            {disclosedSections}
          </div>
          {propertiesPanel}
        </div>

        {alerts}

        {actionBar}
      </form>

      <ConfirmDialog
        open={confirmDiscard}
        title={t('task.createForm.discardTitle')}
        description={t('task.createForm.discardDescription')}
        confirmLabel={t('task.createForm.discardConfirm')}
        cancelLabel={t('task.createForm.discardCancel')}
        destructive
        onConfirm={() => {
          setConfirmDiscard(false);
          discardDraft();
          navigate('/tasks');
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
});

export default TaskCreatePage;
